/* Time is always Khao Lak time (Asia/Bangkok, GMT+7 — no DST, so the offset
   never moves).
   A guest still on New York time who opens the schedule and sees "Thursday"
   because their phone says so has been actively misled, so nothing here ever
   uses the device timezone — every format goes through Asia/Bangkok. */

export const TZ = 'Asia/Bangkok';

/* The wedding is in the future, which makes "next event" and "happening now"
   impossible to see while building. `?at=2026-11-06T10:30` pins the clock for a
   session so those states can be looked at; without it this is just Date.now().
   `components/ClockBar` is the same thing with a picker on it. */
const OVERRIDE_KEY = 'mv.clock';

/**
 * A wall clock in Khao Lak, as milliseconds. `null` if it is not a time.
 *
 * **The offset is appended rather than inferred**, which is the whole point:
 * `2026-11-06T19:45` typed by a person, or handed over by an
 * `<input type="datetime-local">`, means quarter to eight *there*. Left to
 * `Date.parse` it would mean quarter to eight on whatever the laptop is set
 * to, and somebody in London would pin the clock seven hours off the moment
 * they were aiming at and conclude the schedule was wrong.
 *
 * A bare date means midnight there for the same reason. That case used to fall
 * through to `Date.parse('2026-11-06')`, which is midnight *UTC* — so `?at=`
 * with a date and no time landed at 07:00 Khao Lak, which is a working day's
 * difference on a site whose first event is at 17:30.
 *
 * Anything already carrying a zone (`Z`, or an explicit offset) is passed
 * through untouched: it has already said what it means.
 */
export function parseKhaoLak(input: string): number | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  const wall = /^(\d{4}-\d{2}-\d{2})(?:[T ](\d{2}:\d{2})(:\d{2})?)?$/.exec(trimmed);
  const iso = wall
    ? `${wall[1]}T${wall[2] ?? '00:00'}${wall[3] ?? ':00'}+07:00`
    : trimmed;
  const ms = Date.parse(iso);
  // The regex checks the shape, `Date.parse` checks the date — "2026-13-45"
  // passes the first and must not pass the second.
  return Number.isNaN(ms) ? null : ms;
}

/**
 * Take `?at=` out of the address bar once its value has been banked.
 *
 * Every pin ends in a reload, and `readOverride` prefers the URL to storage —
 * so without this, picking a second moment would reload straight back onto the
 * first one, and "put it back" would un-pin and immediately re-pin. Session
 * storage is the single source of truth the moment anything has been stored.
 */
function dropAtParam(): void {
  if (typeof window === 'undefined' || !window.history?.replaceState) return;
  const url = new URL(window.location.href);
  if (!url.searchParams.has('at')) return;
  url.searchParams.delete('at');
  window.history.replaceState(null, '', url);
}

function readOverride(): number | null {
  if (typeof window === 'undefined') return null;
  const fromUrl = new URLSearchParams(window.location.search).get('at');
  if (fromUrl) {
    const ms = parseKhaoLak(fromUrl);
    if (ms !== null) {
      sessionStorage.setItem(OVERRIDE_KEY, String(ms));
      return ms;
    }
  }
  const stored = sessionStorage.getItem(OVERRIDE_KEY);
  return stored ? Number(stored) : null;
}

let override = readOverride();
let overrideSetAt = Date.now();

export function now(): number {
  if (override === null) return Date.now();
  // Let a pinned clock run forwards, so countdowns still tick.
  return override + (Date.now() - overrideSetAt);
}

/** The moment the clock is pinned to, or `null` when it is telling the truth. */
export function pinnedClock(): number | null {
  return override;
}

export function isClockPinned(): boolean {
  return pinnedClock() !== null;
}

/**
 * Pin the clock and leave it to the caller to reload.
 *
 * Nothing re-renders off this: `now()` is read during render, so the page
 * already on screen keeps whatever it sampled. A reload is what makes the new
 * moment take, and it is the caller's call because the caller is the one that
 * knows whether anything else is half-typed.
 */
export function pinClock(ms: number): void {
  sessionStorage.setItem(OVERRIDE_KEY, String(ms));
  dropAtParam();
  override = ms;
  overrideSetAt = Date.now();
}

export function clearPinnedClock(): void {
  sessionStorage.removeItem(OVERRIDE_KEY);
  dropAtParam();
  override = null;
  overrideSetAt = Date.now();
}

const timeFmt = new Intl.DateTimeFormat('en-GB', { timeZone: TZ, hour: '2-digit', minute: '2-digit', hour12: false });
const dayFmt = new Intl.DateTimeFormat('en-GB', { timeZone: TZ, weekday: 'long', day: 'numeric', month: 'short' });
const dayLongFmt = new Intl.DateTimeFormat('en-GB', { timeZone: TZ, weekday: 'long', day: 'numeric', month: 'long' });
/** "Thu 5" — for a control too narrow for `fmtDay`'s "Thursday 5 Nov". */
const dayShortFmt = new Intl.DateTimeFormat('en-GB', { timeZone: TZ, weekday: 'short', day: 'numeric' });
const keyFmt = new Intl.DateTimeFormat('en-CA', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit' });

/**
 * The inverse of `parseKhaoLak`, in the one format
 * `<input type="datetime-local">` accepts: `2026-11-06T19:45`.
 *
 * Its own formatter rather than `dayKey` + `fmtTime` glued together, for
 * `hourCycle: 'h23'`. `hour12: false` alone is under-specified and some ICU
 * builds answer `24:00` for midnight — which is a legal thing for a person to
 * read and an illegal value for the input, so the field would silently come up
 * empty on exactly the moment this was built to look at.
 */
const localFmt = new Intl.DateTimeFormat('en-GB', {
  timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
});

export function toKhaoLakLocal(ms: number): string {
  const p = Object.fromEntries(localFmt.formatToParts(ms).map((x) => [x.type, x.value]));
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}

export const fmtTime = (iso: string | number) => timeFmt.format(new Date(iso));
export const fmtDay = (iso: string | number) => dayFmt.format(new Date(iso));
export const fmtDayLong = (iso: string | number) => dayLongFmt.format(new Date(iso));
export const fmtDayShort = (iso: string | number) => dayShortFmt.format(new Date(iso));
export const dayKey = (iso: string | number) => keyFmt.format(new Date(iso));

/**
 * Which day something falls on in Khao Lak, as a whole number.
 *
 * Built from `dayKey`, so it asks the same question the rest of this file does
 * — what is the date *there* — rather than doing arithmetic on a timestamp and
 * hoping the offset lines up.
 */
const DAY_MS = 86_400_000;

const dayNumber = (ms: number) => Math.round(Date.parse(`${dayKey(ms)}T00:00:00Z`) / 86400000);

/**
 * "in 2 hours" · "in 25 minutes" · "starting now" · null once it has started.
 *
 * **Past a day, this counts calendar days in Khao Lak — sleeps, not elapsed
 * time.** It used to round elapsed hours, `Math.round(hours / 24)`, and that
 * was wrong in a way that showed: the countdown on the same screen floors a
 * duration to midnight on the 5th, this rounded a duration to a 17:30 dinner,
 * and between about 03:00 and 09:00 Khao Lak the two differed **by two whole
 * days** — "54 days" above "Next · in 56 days". Rounding also lets the label
 * overstate the wait, saying "in 56 days" for something 55 days and twelve
 * hours away, which is not what anybody means by it.
 *
 * A calendar count fixes both. It cannot overstate, it does not flip between
 * two answers as the morning wears on, and it is what "in 55 days" means when
 * somebody says it out loud: fifty-five sleeps, whatever hour it happens to be.
 * The countdown keeps its own precise duration, and the gap between them is
 * now always the remainder the countdown is already showing in its own hours
 * column.
 */
export function untilLabel(startIso: string, at = now()): string | null {
  const start = Date.parse(startIso);
  const diff = start - at;
  if (diff <= 0) return null;
  const mins = Math.round(diff / 60000);
  if (mins <= 1) return 'starting now';
  if (mins < 60) return `in ${mins} minutes`;
  const hours = Math.round(mins / 60);
  // **Branched on the real distance, not on the rounded hours.** Rounding first
  // sent anything from 23h30m upwards into the day branch, where an event later
  // the same evening came out as "in 1 day" — and needed a `max(1, …)` clamp to
  // stop it being "in 0 days". Asking the actual duration keeps those the
  // hours they are ("in 24 hours"), and makes the clamp genuinely unreachable:
  // a full day always crosses a midnight, so past this line the two dates
  // differ by at least one.
  if (diff < DAY_MS) return `in ${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  const days = dayNumber(start) - dayNumber(at);
  return `in ${days} ${days === 1 ? 'day' : 'days'}`;
}

export function durationLabel(startIso: string, endIso?: string): string | null {
  if (!endIso) return null;
  const mins = Math.round((Date.parse(endIso) - Date.parse(startIso)) / 60000);
  if (mins < 60) return `${mins} minutes`;
  const hours = mins / 60;
  const rounded = Math.round(hours * 2) / 2;
  return `${rounded} ${rounded === 1 ? 'hour' : 'hours'}`;
}

/** How old a cached payload is, said plainly: "from 19:12". */
export function cacheAgeLabel(fetchedAt: number): string {
  return `from ${fmtTime(fetchedAt)}`;
}

/**
 * A day, not a time: "just now", "today", or the date ("Friday 18 Sep").
 *
 * For the organiser roster's "Claimed" and "Seen", where the question is which
 * day, not which minute, and a clock time on every card was noise. A few
 * minutes counts as "just now" because "4 min ago" says nothing "today" does
 * not, and it would be stale the moment it was read.
 */
export function relativeDay(ms: number, at = now()): string {
  // Within five minutes **either side**. A stamp ahead of this device's clock
  // (a phone set wrong, a server a little ahead) must not read "just now" for
  // ever: `at - ms` is negative for every future time, so a one-sided check
  // would put every such row there. Beyond the window, the day speaks for it.
  if (Math.abs(at - ms) < 5 * 60_000) return 'just now';
  if (dayKey(ms) === dayKey(at)) return 'today';
  return fmtDay(ms);
}

export function relativeUploadTime(ms: number, at = now()): string {
  const diff = at - ms;
  const mins = Math.round(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  if (dayKey(ms) === dayKey(at)) return `today, ${fmtTime(ms)}`;
  return `${fmtDay(ms)}, ${fmtTime(ms)}`;
}
